import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { defaultTransition } from '../../theme'

import { toast } from 'react-toastify'

import { Box } from '../UI/Box'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import AnimatedLoader from '../AnimatedLoader'
import ThemedButton from '../ThemedButton'
import useWeb3 from '../../hooks/useWeb3'
import { useSocialAccounts } from '@dynamic-labs/sdk-react-core'
import { ProviderEnum } from '@dynamic-labs/sdk-api-core'

const Link = styled.span`
  span.active {
    color: ${({ theme }) => theme.colors.brandPrimary};
    ${defaultTransition}
    :hover {
      cursor: pointer;
      color: ${({ theme }) => theme.colors.brandSecondary};
    }
  }
`

const ConnectedAccount = styled(Box)`
  flex-grow: 0;
  button {
    ${defaultTransition}
  }
`

const TwitterConnectButton = ({ label = 'Connect' }: { label?: string }) => {
  const {
    getLinkedAccountInformation,
    linkSocialAccount,
    unlinkSocialAccount,
    isProcessing,
    isLinked,
  } = useSocialAccounts()
  const { initWeb3 } = useWeb3()

  const { user, isLoggedIn } = useIsLoggedIn()

  const { twitterHandle: _userTwitterHandle } = user ?? { twitterHandle: '' }
  const [twitterHandle, setTwitterHandle] = useState(user?.twitterHandle)
  useEffect(() => {
    const acc = getLinkedAccountInformation(ProviderEnum.Twitter)
    const { displayName, username } = acc ?? { displayName: '', username: '' }
    setTwitterHandle(_userTwitterHandle || displayName || username)
  }, [getLinkedAccountInformation, ProviderEnum.Twitter, _userTwitterHandle])

  const connectTwitter = useCallback(async () => {
    try {
      await linkSocialAccount(ProviderEnum.Twitter)
    } catch (err) {
      console.error(err)
      toast.error('Failed to connect Twitter')
    }
  }, [linkSocialAccount])

  async function disconnectTwitter() {
    try {
      await unlinkSocialAccount(ProviderEnum.Twitter)
    } catch (err) {
      console.error(err)
      toast.error('Failed to disconnect Twitter')
    }
  }

  if (!isLoggedIn) {
    return (
      <Link>
        <ThemedButton text="Connect" onClick={() => initWeb3()} />
      </Link>
    )
  }

  if (!!isLinked(ProviderEnum.Twitter)) {
    return (
      <ConnectedAccount ac g={16} className="force-row">
        {twitterHandle}
        <ThemedButton
          $styleType="secondary"
          onClick={disconnectTwitter}
          text={isProcessing ? <AnimatedLoader size={16} /> : 'Disconnect'}
        />
      </ConnectedAccount>
    )
  }

  return (
    <Link>
      <ThemedButton
        $styleType="surface-chip"
        onClick={connectTwitter}
        text={isProcessing ? <AnimatedLoader size={16} /> : label}
      />
    </Link>
  )
}

export default TwitterConnectButton

export function obfuscateEthWalletAddress(
  address: string,
  includeFullAddress = true,
) {
  if (!address) return 'UNKOWN'
  return `${address.slice(0, 6)}...${
    includeFullAddress ? address.slice(-4) : ''
  }`
}
